import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFetch } from "../../hooks/api";
import { Row, Col, Container as Grid } from "react-bootstrap";

import { Accordion } from "../../components/core/basic/Accordion";

import { useModuleEnabled } from "../../redux/selectors/modules";
import checkTierAccess from "../../utils/checkTierAccess";
import SEOChart from "../../modules/product_quality/components/SEOScoreChart";
import BuyabilityChart from "../../modules/product_quality/components/BuyabilityScoreChart";

import { useParams } from "@tanstack/react-router";
import { TooltipBuyability, TooltipSearchability } from "../../modules/product_quality/components/Tooltips";
import Loading from "../../components/core/blocks/Loading";
import PaymentDiscovery from "../billing/PaymentDiscovery";
import BreadcrumbDates from "../../components/core/blocks/BreadcrumbDates";
import { DatesProvider } from "../../dates/DatesContext";
import { useDates } from "../../dates/DatesContext";
import { useDateFormatter } from "../../i18n/useDateFormatter";
import useCategories from "../../hooks/useCategories";
import getMetricSpec from "../../modules/merchandising/utils/metrics";
import MetricsCard from "../../components/custom/merchandise/MetricsCard";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useGetV4CategoryReportByDate } from "../../modules/perf_reports/api/category/v4CategoryReportByDate";
import { useGetV4CategoryReportTotals } from "../../modules/perf_reports/api/category/v4CategoryReportTotals";
import TemplatesSection from "../../modules/product_quality/components/TemplatesSection";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "../../utils/dashboardLinks";
import { useAppSelector } from "../../redux/hooks";
import { ContentCategoriesTable } from "./components/ContentCategoriesTable";
import { ContentProductsTable } from "./components/ContentProductsTable";

const getDashboardTo = () => {
  return "/user/content/quality/org";
};

const OrgQuality = () => {
  const { t } = useTranslation(["product_quality", "common"]);
  const { category = null } = useParams({ strict: false });
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel);
  const user = useAppSelector((state) => state.user);
  const amz_onboard = useAppSelector((state) => state.amz_onboard);
  const hasProductSeoModule = useModuleEnabled("PRODUCT_SEO");
  const { group, start, end, preStart, preEnd } = useDates();
  const longDate = useDateFormatter("long");

  const { data: orgData, isLoading: orgDataLoading } = useFetch(
    ["productQualityOrgs", start, end, group, preStart, preEnd, channel, category],
    `/merchandise/content_report/`,
    {
      channel: "AMZ",
      market_place: marketPlace.marketPlace,
      group_by: group,
      start: start.format("YYYY-MM-DD"),
      end: end.format("YYYY-MM-DD"),
      pre_end: preEnd.format("YYYY-MM-DD"),
      pre_start: preStart.format("YYYY-MM-DD"),
      category: category,
    },
    { select: (d) => d.data },
  );

  const categoryFilters = useMemo(
    () => (category ? [{ id: "category_id", value: { op: "=", value: category } }] : []),
    [category],
  );

  const getCategoryTotalsOptions = useGetV4CategoryReportTotals();
  const categoryTotalsQuery = useQuery({
    ...getCategoryTotalsOptions({ filters: categoryFilters }),
    placeholderData: keepPreviousData,
  });

  const getCategoryByDateOptions = useGetV4CategoryReportByDate();
  const categoryByDateQuery = useQuery({
    ...getCategoryByDateOptions({ filters: categoryFilters }),
    placeholderData: keepPreviousData,
  });

  const { currentCategoryDetails: categoryDetails, categories: categoryNavData } = useCategories(
    category,
    "content/quality",
  );

  const buckets = useMemo(() => {
    const buckets = [];
    if (orgData?.total) {
      for (let i = 0; i < 4; i++) {
        buckets.push({
          seo_count: orgData.total?.[`seo_count_${i}_avg`],
          buyability_count: orgData.total?.[`buyability_count_${i}_avg`],
          cr: orgData.total?.[`cr_${i}_avg`],
          ctr: orgData.total?.[`ctr_${i}_avg`],
        });
      }
    }
    return buckets;
  }, [orgData]);

  const metrics = useMemo(() => {
    return [
      getMetricSpec("sales"),
      getMetricSpec("units"),
      getMetricSpec("seo_score"),
      getMetricSpec("ctr"),
      getMetricSpec("buyability_score"),
      getMetricSpec("conversion_rate"),
      //getMetricSpec("page_views"),
    ];
  }, []);

  const CONTENT_SUBSCRIPTION =
    ((user.content_tier &&
      user.content_tier !== "unknown" &&
      (amz_onboard.org_status === "active" ||
        amz_onboard.org_status === "trial_active" ||
        amz_onboard.org_status === "demo_org")) ||
      hasProductSeoModule ||
      checkTierAccess(user.tier, "content", amz_onboard.org_status)) &&
    user.content_tier !== "MANUALLY_DISABLED";

  if (!CONTENT_SUBSCRIPTION) {
    return <PaymentDiscovery discoveryType={"advertising"} dashboardType="content" />;
  }

  const breadCrumbItems = [
    AGENCY_DASHBOARD_BREADCRUMB_SPEC,
    {
      name: t(($) => $.product_quality["Content Dashboard"]),
      href: getDashboardTo(),
      dropdownLinkOptions: DASHBOARD_LINKS,
      unsorted: true,
      searchable: false,
      dropdownSubtitle: t(($) => $.product_quality["Dashboards"]),
    },
  ];

  if (category) {
    breadCrumbItems.push({
      name: categoryDetails?.leaf_category_name,
      href: categoryDetails.href,
      dropdownLinkOptions: categoryNavData,
      searchable: true,
      searchPlaceholderText: t(($) => $.product_quality["Search categories..."]),
      dropdownSubtitle: t(($) => $.product_quality["Categories"]),
    });
  }

  return (
    <Grid
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      <Row>
        <Col xs={12} className="mb-4">
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="mb-4">
          <MetricsCard
            title={t(($) => $.product_quality["Content"])}
            byline={
              <>
                {longDate.format(start.toDate())}
                {" - "}
                {longDate.format(end.toDate())}
              </>
            }
            metrics={metrics}
            totals={{
              data: categoryTotalsQuery.data?.data,
              loading: categoryTotalsQuery.isFetching,
            }}
            timeline={{
              data: categoryByDateQuery.data?.data ?? [],
              loading: categoryByDateQuery.isFetching,
            }}
            initialSelectedMetric={["sales", "seo_score", "buyability_score"]}
            group={group}
            showAnnotations={true}
            showMetricsSelector
            id={"contentDashboard"}
            context={category ? "product" : "category"}
          >
            <Row>
              <Col xs={12} md={6}>
                <div className="text-center">
                  <h4>
                    {t(($) => $.product_quality["Searchability"])} <TooltipSearchability />
                  </h4>
                </div>
                {orgDataLoading ?
                  <Loading fullPage={false} height="260px" />
                : <SEOChart seoData={buckets} />}
              </Col>
              <Col xs={12} md={6}>
                <div className="text-center">
                  <h4>
                    {t(($) => $.product_quality["Buyability"])} <TooltipBuyability />{" "}
                  </h4>
                </div>
                {orgDataLoading ?
                  <Loading fullPage={false} height="260px" />
                : <BuyabilityChart buyabilityData={buckets} />}
              </Col>
            </Row>

            <Row className="pt-5 fs-standard">
              <Col xs={12} className="text-end">
                <Accordion
                  title={category ? t(($) => $.product_quality["Products"]) : t(($) => $.product_quality["Categories"])}
                  defaultExpanded
                >
                  {category ?
                    <ContentProductsTable category={category} />
                  : <ContentCategoriesTable />}
                </Accordion>
              </Col>
            </Row>
            {!category && (
              <Row className="pt-5 fs-standard">
                <Col xs={12} className="text-end">
                  <TemplatesSection marketPlace={marketPlace} />
                </Col>
              </Row>
            )}
          </MetricsCard>
        </Col>
      </Row>
    </Grid>
  );
};

const OrgQualityDashboard = () => {
  return (
    <DatesProvider>
      <OrgQuality />
    </DatesProvider>
  );
};

export default OrgQualityDashboard;
